/**
 * <nia-steps>
 * Marca los pasos de un método como hechos y guarda el avance en
 * sessionStorage, no en localStorage: una preparación es de una sola sesión, y
 * volver mañana con los cuatro pasos ya tachados sería confuso.
 *
 * El estado visual lo maneja el CSS con :has(input:checked); este componente
 * solo se ocupa de la barra, el conteo, el botón de reinicio y la persistencia.
 */
class NiaSteps extends HTMLElement {
  connectedCallback() {
    this.checks = Array.from(this.querySelectorAll('[data-steps-check]'));
    this.fill = this.querySelector('[data-steps-fill]');
    this.counter = this.querySelector('[data-steps-count]');
    this.reset = this.querySelector('[data-steps-reset]');
    this.total = Number(this.dataset.total) || this.checks.length;
    this.storageKey = `nia_ritual_${this.dataset.key || 'metodo'}`;

    this.addEventListener('change', this.#onChange);
    this.reset?.addEventListener('click', this.#onReset);

    this.#restore();
    this.#paint();
  }

  get #doneCount() {
    return this.checks.filter((check) => check.checked).length;
  }

  #restore() {
    let saved = null;
    try {
      saved = sessionStorage.getItem(this.storageKey);
    } catch (_) {
      // Sin storage disponible, el avance simplemente no se recuerda.
    }
    if (!saved) return;

    const done = new Set(saved.split(','));
    this.checks.forEach((check) => {
      if (done.has(check.value)) check.checked = true;
    });
  }

  #save() {
    try {
      const done = this.checks.filter((check) => check.checked).map((check) => check.value);
      if (done.length === 0) sessionStorage.removeItem(this.storageKey);
      else sessionStorage.setItem(this.storageKey, done.join(','));
    } catch (_) {
      // no-op
    }
  }

  #paint() {
    const done = this.#doneCount;
    const pct = this.total > 0 ? (done / this.total) * 100 : 0;

    if (this.fill) this.fill.style.width = `${pct}%`;
    if (this.counter) this.counter.textContent = `${done}/${this.total}`;
    if (this.reset) this.reset.hidden = done === 0;
  }

  #onChange = (event) => {
    if (!event.target.matches('[data-steps-check]')) return;
    this.#save();
    this.#paint();
  };

  #onReset = () => {
    this.checks.forEach((check) => {
      check.checked = false;
    });
    this.#save();
    this.#paint();
    this.checks[0]?.focus();
  };
}

if (!customElements.get('nia-steps')) {
  customElements.define('nia-steps', NiaSteps);
}
